// Structural equality + a "keep the previous reference" hook. The sim publishes a fresh snapshot
// (and fresh tile / detail objects) every tick, so reference equality never bails; without this
// the canvas would repaint on every tick even when nothing visible changed.

import { useRef } from 'react';
import type { PreparedBoard } from './minimapBoard.js';
import type { Point } from './minimapGeometry.js';
import type {
  MinimapCityInfo,
  MinimapDetails,
  MinimapMarker,
  MinimapPing,
  MinimapTile,
  MinimapUnitInfo,
} from './types.js';

export function minimapTileEquals(a?: MinimapTile, b?: MinimapTile): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.q === b.q &&
    a.r === b.r &&
    a.biome === b.biome &&
    a.controlledBy === b.controlledBy &&
    a.visibility === b.visibility &&
    a.hasCity === b.hasCity &&
    a.hasUnit === b.hasUnit &&
    // Rivers and terrain level are painted on the STATIC layer, so a bridge
    // (a river mask that changes) or a terrain-level change has to count as a
    // change — otherwise the cached static layer keeps the old rivers.
    (a.riverEdges ?? 0) === (b.riverEdges ?? 0) &&
    (a.terrainLevel ?? 0) === (b.terrainLevel ?? 0)
  );
}

export function boardEquals(a: PreparedBoard, b: PreparedBoard): boolean {
  if (a === b) return true;
  if (a.tiles.length !== b.tiles.length) return false;
  for (let i = 0; i < a.tiles.length; i++) {
    if (!minimapTileEquals(a.tiles[i], b.tiles[i])) return false;
  }
  return true;
}

function mapEquals<V>(
  a: ReadonlyMap<number, V> | undefined,
  b: ReadonlyMap<number, V> | undefined,
  eq: (x: V, y: V) => boolean,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.size !== b.size) return false;
  for (const [k, v] of a) {
    const w = b.get(k);
    if (w === undefined || !eq(v, w)) return false;
  }
  return true;
}

const cityEq = (a: MinimapCityInfo, b: MinimapCityInfo) =>
  a.ownerId === b.ownerId &&
  a.name === b.name &&
  a.tier === b.tier &&
  a.isCapital === b.isCapital &&
  a.neutral === b.neutral;
const unitEq = (a: MinimapUnitInfo, b: MinimapUnitInfo) =>
  a.ownerId === b.ownerId &&
  a.count === b.count &&
  a.ownCount === b.ownCount &&
  a.color === b.color;

export function detailsEqual(a?: MinimapDetails, b?: MinimapDetails): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return mapEquals(a.cities, b.cities, cityEq) && mapEquals(a.units, b.units, unitEq);
}

export function markersEqual(a?: readonly MinimapMarker[], b?: readonly MinimapMarker[]): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((m, i) => {
    const n = b[i];
    return (
      n !== undefined &&
      m.q === n.q &&
      m.r === n.r &&
      m.color === n.color &&
      m.pulsing === n.pulsing &&
      m.glyph === n.glyph
    );
  });
}

export function colorsEqual(
  a?: ReadonlyMap<number, string>,
  b?: ReadonlyMap<number, string>,
): boolean {
  return mapEquals(a, b, (x, y) => x === y);
}

/** Active pings are equal when the same tiles/severities are ringing. */
export function pingsEqual(a?: readonly MinimapPing[], b?: readonly MinimapPing[]): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return (
      q !== undefined &&
      p.q === q.q &&
      p.r === q.r &&
      p.severity === q.severity &&
      Math.abs(p.startedAt - q.startedAt) < 1
    );
  });
}

/** Unit-space points (the camera view box) are equal when every point matches. */
export function polygonEqual(a?: readonly Point[] | null, b?: readonly Point[] | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return q !== undefined && Math.abs(p.x - q.x) < 1e-4 && Math.abs(p.y - q.y) < 1e-4;
  });
}

/** A single unit-space point (the camera focus) is equal when both axes match. */
export function pointEqual(a?: Point | null, b?: Point | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return Math.abs(a.x - b.x) < 1e-4 && Math.abs(a.y - b.y) < 1e-4;
}

/** Returns the previous value while `eq(prev, next)` holds, so downstream memo/effects bail. */
export function useStable<T>(next: T, eq: (a: T, b: T) => boolean): T {
  const ref = useRef(next);
  if (ref.current !== next && !eq(ref.current, next)) ref.current = next;
  return ref.current;
}
